import { createContext, FCWithChildren, useCallback, useMemo, useRef, useState } from 'react';
import { nameof } from '../utils/stringUtils';

interface IStoredData {
  data: unknown;
  deleteTimeOut: NodeJS.Timeout | undefined;
}

interface IContextValue {
  /**
  @param {string} key The key used to retrieve this data
  @param {T} data The data to be stored
  @param {'session' | number} ttl Either the string "session" or a number in seconds
  */
  storeData<T>(key: string, data: T, ttl: 'session' | number): void;
  retrieveData<T>(key: string): T | undefined;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const TemporaryDataCacheContext = createContext<IContextValue>(undefined!);

const TemporaryDataCacheProvider: FCWithChildren = ({ children }) => {

  const [store, setStore] = useState<Record<string, IStoredData>>({});
  const storeRef = useRef(store);
  storeRef.current = store;

  const deleteData = useCallback(
    (key: string) => setStore(curr => {
      const newStore = { ...curr };
      delete newStore[key];
      return newStore;
    }),
    [],
  );

  const storeData = useCallback<IContextValue['storeData']>(
    (key, data, ttl) => {
      const foundData = storeRef.current[key];

      if (foundData !== undefined && foundData.deleteTimeOut !== undefined)
        clearTimeout(foundData.deleteTimeOut);

      setStore(curr => ({
        ...curr,
        [key]: {
          data,
          deleteTimeOut: ttl !== 'session'
            ? setTimeout(() => deleteData(key), ttl * 1000)
            : undefined,
        },
      }));
    },
    [deleteData],
  );

  const retrieveData = useCallback(
    // eslint-disable-next-line @typescript-eslint/comma-dangle
    <T,>(key: string) => {
      const foundData = storeRef.current[key];

      if (foundData !== undefined)
        return foundData.data as T;

      return undefined;
    },
    [],
  );

  const value = useMemo<IContextValue>(
    () => ({ storeData, retrieveData }),
    [retrieveData, storeData],
  );

  return (
    <TemporaryDataCacheContext.Provider value={value}>
      {children}
    </TemporaryDataCacheContext.Provider>
  );
};

TemporaryDataCacheProvider.displayName = nameof({ TemporaryDataCacheProvider });
export default TemporaryDataCacheProvider;